import React from 'react';

import {RTKDetailPageGenericTeamsSidebar} from 'app/components/general/RTKDetailPageGenericTeamsSidebar';
import {useInfiniteListFetcherRTK} from 'app/general/datafetchers/hooks/useInfiniteListFetcherRTK';
import {
  useAddProcessesToTeam,
  useAddQuizzesToTeam,
  useRemoveTeamItem,
  useTeams,
} from 'app/teams/api';
import {documentTeamVia} from 'app/teams/selectors';
import {convertLocationParamsToFetchParams} from 'app/teams/utils';
import {SORT_BY_TYPES} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import type {TeamMembershipSerializedType} from 'sweet-types';

type Props = {
  id: number | string;
  url: string;
  canEdit: boolean;
  onClose: () => void;
  contentType: 'procedure' | 'proceduregroup' | 'policy' | 'quiz';
  team_memberships: Array<TeamMembershipSerializedType>;
};

export const RTKDocumentTeamsSidebar = ({
  id,
  url,
  contentType,
  canEdit,
  team_memberships,
  ...other
}: Props) => {
  const {results: attachedTeams} = useInfiniteListFetcherRTK({
    fetchParams: {
      document_url: url,
      ordering: SORT_BY_TYPES.CREATION_DATE,
    },
    useDataHook: useTeams,
    paramsConverter: convertLocationParamsToFetchParams,
  });
  const useAsyncActions = {
    quiz: useAddQuizzesToTeam,
    proceduregroup: useAddProcessesToTeam,
  };
  const [addDocToTeam] = useAsyncActions[contentType]();
  const [removeTeamItem] = useRemoveTeamItem();

  const props = {
    objectId: id,
    objectContentType: contentType,
    objectURL: url,
    attachedTeams,
    addToTeam: (item) => {
      return addDocToTeam({team: item, urls: [url]}).unwrap();
    },
    removeFromTeam: (item) => {
      return removeTeamItem({teamURL: item.url, item: {url, team_memberships}});
    },
    canEditObject: canEdit,
    objectTeamsMembership: team_memberships,
    itemComponentProps: (team, attached) => {
      const via = documentTeamVia(team_memberships, team);
      return {
        disableAndShowTooltipText:
          attached && via.via == 'process'
            ? gt(
                'The document was added to this team via a process %s',
                via.name
              )
            : attached && via.via == 'folder'
            ? gt(
                'The document was added to this team via a folder %s',
                via.name
              )
            : !canEdit
            ? gt('You must be a manager of this document')
            : !team.can_edit
            ? gt('Only a manager can add and remove from this team')
            : undefined,
        actionTooltip: attached
          ? contentType != 'policy'
            ? gt(
                'Remove document from this team (this will also cancel any tasks related to the team.)'
              )
            : gt('Remove document from this team.')
          : gt('Add document to this team'),
      };
    },
    onModelChange: () => [],
    ...other,
  };
  return <RTKDetailPageGenericTeamsSidebar {...props} />;
};
